import { useCallback, useRef, useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { default as ky, HTTPError } from "ky"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import useSysConfig from "@/hooks/useSysConfig"
import { API_PREFIX } from "@/constants"
import { getLimitErrorTranslationKey } from "@/constants/errors"
import {
  getApiErrorData,
  getApiErrorPresentationProps,
  getUserFacingApiErrorMessage,
  type ApiErrorData,
} from "@/lib/apiErrors"
import {
  isValidClientUserIdentity,
  isValidEmail,
  validateUserFields,
} from "@/lib/userValidation"
import useToast from "@/hooks/useToast"
import { useDebounce } from "@/hooks"
import { useStore } from "@/store"
import {
  useGetClientAccessApi,
  useUpdateClientAccessApi,
  useUpdateWorkspacesApi,
  useListClientUsersApi,
  useListClientUserTagsApi,
  useGetClientUserTagUsageCountsApi,
  useCreateClientUserTagApi,
  useDeleteClientUserTagApi,
  useSetClientUserTagsApi,
  useCreateClientUserApi,
  usePreviewImportClientUsersCsvApi,
  useConfirmImportClientUsersCsvApi,
  useBatchClientUsersApi,
  useGetClientChannelsApi,
  useUpdateWorkspaceEndUserIdentityMaskingApi,
  useListReplyModeAgentsApi,
  type ClientAccessMode,
  type ReplyModeBatch,
  type ReplyModeBatchFilters,
  type ReplyModeTarget,
  type ClientUserTag,
  type ClientUserListItem,
  type ClientUserCsvPreviewResponse,
  workspaceResponseToWorkspace,
} from "@/hooks/api"
import { FetchStatus } from "@/hooks/useFetch"
import {
  OrganizationRole,
  Permission,
  WorkspaceRole,
  type Workspace,
} from "@/types"
import PageNav from "@/components/PageNav"
import UploadDropzone from "@/components/UploadDropzone"
import { PermissionButton } from "@/components/Auth"
import {
  Upload,
  ChevronUp,
  ChevronDown,
  UserPlus,
  FileText,
  CheckCircle2,
  XCircle,
  Info,
  Users,
  Tags,
  Pencil,
  Trash2,
  Shield,
} from "lucide-react"
import { OnboardingEmptyState } from "@/components/System"
import ChannelTypeBadge from "@/pages/Workspace/components/Channel/ChannelTypeBadge"
import { ClientChannelType } from "@/types"
import {
  ClientUserTagDeleteDialog,
  ClientUserTagFilter,
  ClientUserTagInlineEditor,
  ClientUserTagManagerDialog,
  ReplyModeAgentSelect,
  ReplyModeBatchResults,
  ReplyModeSelectionActions,
  useReplyModeBatch,
} from "./UserManagement"

const RM = "crm.userManagement.replyMode"

export interface UserManagementTabProps {
  workspace: Workspace
  onWorkspaceUpdated?: (workspace: Workspace) => void
}

type SortField =
  | "external_user_id"
  | "last_login_at"
  | "created_at"
  | "conversation_count"
type SortOrder = "asc" | "desc"
type CreditCapMessageLocale = "en" | "zh-TW" | "ja"

const CREDIT_CAP_MESSAGE_LOCALES: CreditCapMessageLocale[] = [
  "en",
  "zh-TW",
  "ja",
]

const getCreditCapMessageInput = (
  messages?: Partial<Record<CreditCapMessageLocale, string>> | null
): Record<CreditCapMessageLocale, string> => ({
  en: messages?.en || "",
  "zh-TW": messages?.["zh-TW"] || "",
  ja: messages?.ja || "",
})

const buildCreditCapMessagePayload = (
  messages: Record<CreditCapMessageLocale, string>
) => {
  const normalized = Object.fromEntries(
    CREDIT_CAP_MESSAGE_LOCALES.map((locale) => [
      locale,
      messages[locale].trim(),
    ]).filter(([, message]) => !!message)
  ) as Partial<Record<CreditCapMessageLocale, string>>

  return Object.keys(normalized).length > 0 ? normalized : null
}

const getContactEmailInput = (workspace: Workspace) =>
  workspace.contactEmail || ""

const isClientChannelType = (
  value: string | null
): value is ClientChannelType =>
  !!value &&
  Object.values(ClientChannelType).includes(value as ClientChannelType)

const CLIENT_USER_CHANNEL_TYPES: ClientChannelType[] = [
  ClientChannelType.WEB,
  ClientChannelType.WIDGET,
  ClientChannelType.BROWSER_EXTENSION,
  ClientChannelType.SLACK,
  ClientChannelType.LINE,
  ClientChannelType.MESSENGER,
  ClientChannelType.INSTAGRAM,
]

interface ChannelIdentityFieldConfig {
  labelKey: string
  helperKey: string
  placeholder: string
  example: string
}

interface ClientUserChannelOption {
  value: string
  channelType: ClientChannelType
  clientChannelId?: string
  channelName?: string
  channelSlug?: string
  allowAnonymousAccess?: boolean
}

const requiresGoogleLoginEmail = (option?: ClientUserChannelOption) =>
  option?.channelType === ClientChannelType.BROWSER_EXTENSION ||
  (option?.channelType === ClientChannelType.WEB &&
    option.allowAnonymousAccess === false)

const DEFAULT_CHANNEL_IDENTITY_FIELD: ChannelIdentityFieldConfig = {
  labelKey: "crm.userManagement.addUser.identityFields.generic.label",
  helperKey: "crm.userManagement.addUser.identityFields.generic.helper",
  placeholder: "user-123",
  example: "user-123",
}

const CHANNEL_IDENTITY_FIELDS: Partial<
  Record<ClientChannelType, ChannelIdentityFieldConfig>
> = {
  [ClientChannelType.WIDGET]: {
    labelKey: "crm.userManagement.addUser.identityFields.widget.label",
    helperKey: "crm.userManagement.addUser.identityFields.widget.helper",
    placeholder: "customer-123",
    example: "customer-123",
  },
  [ClientChannelType.SLACK]: {
    labelKey: "crm.userManagement.addUser.identityFields.slack.label",
    helperKey: "crm.userManagement.addUser.identityFields.slack.helper",
    placeholder: "user@example.com or U012ABC",
    example: "user@example.com",
  },
  [ClientChannelType.LINE]: {
    labelKey: "crm.userManagement.addUser.identityFields.line.label",
    helperKey: "crm.userManagement.addUser.identityFields.line.helper",
    placeholder: "U0123456789abcdef0123456789abcdef",
    example: "U0123456789abcdef0123456789abcdef",
  },
  [ClientChannelType.MESSENGER]: {
    labelKey: "crm.userManagement.addUser.identityFields.messenger.label",
    helperKey: "crm.userManagement.addUser.identityFields.messenger.helper",
    placeholder: "1234567890123456",
    example: "1234567890123456",
  },
  [ClientChannelType.INSTAGRAM]: {
    labelKey: "crm.userManagement.addUser.identityFields.instagram.label",
    helperKey: "crm.userManagement.addUser.identityFields.instagram.helper",
    placeholder: "1234567890123456",
    example: "1234567890123456",
  },
}

const CSV_GOOGLE_LOGIN_IDENTITY_FIELD: ChannelIdentityFieldConfig = {
  labelKey: "crm.userManagement.addUser.googleLoginEmail",
  helperKey: "crm.userManagement.addUsers.csvImport.webWhitelistIdentityHelper",
  placeholder: "john@example.com",
  example: "john@example.com",
}

const getChannelIdentityField = (
  channelType: ClientChannelType | ""
): ChannelIdentityFieldConfig =>
  (channelType && CHANNEL_IDENTITY_FIELDS[channelType]) ||
  DEFAULT_CHANNEL_IDENTITY_FIELD

interface UserIdentityLine {
  labelKey: string
  value: string
}

const getDefaultClientUserChannelValue = (
  options: ClientUserChannelOption[],
  configuredChannels: ClientUserChannelOption[],
  channelsLoaded: boolean
): string => {
  if (!channelsLoaded) return ""
  if (configuredChannels.length === 0) {
    return (
      options.find((option) => option.channelType === ClientChannelType.WEB)
        ?.value || ""
    )
  }

  const configuredTypes = [
    ...new Set(configuredChannels.map((option) => option.channelType)),
  ]
  if (configuredTypes.length !== 1) return ""

  const configuredType = configuredTypes[0]
  const matchingOptions = configuredChannels.filter(
    (option) => option.channelType === configuredType
  )
  if (configuredType === ClientChannelType.WEB && matchingOptions.length > 1) {
    return ""
  }
  if (matchingOptions.length === 1) return matchingOptions[0].value

  const fallbackOption = options.find(
    (option) => option.channelType === configuredType
  )
  if (fallbackOption) return fallbackOption.value
  return ""
}

export const UserManagementTab = ({
  workspace,
  onWorkspaceUpdated,
}: UserManagementTabProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const config = useSysConfig()
  const [currentOrganization] = useStore((s) => [s.currentOrganization])

  const [accessMode, setAccessMode] = useState<ClientAccessMode>("open")
  const [accessModeLoaded, setAccessModeLoaded] = useState(false)
  const [accessModeLoadFailed, setAccessModeLoadFailed] = useState(false)
  const [pendingAccessMode, setPendingAccessMode] =
    useState<ClientAccessMode | null>(null)
  const [showAccessModeConfirmDialog, setShowAccessModeConfirmDialog] =
    useState(false)
  const [showAccessModeOpenWarningDialog, setShowAccessModeOpenWarningDialog] =
    useState(false)
  const [showAccessModeOpenConfirmDialog, setShowAccessModeOpenConfirmDialog] =
    useState(false)
  const [openConfirmInput, setOpenConfirmInput] = useState("")
  const [isUpdatingAccessMode, setIsUpdatingAccessMode] = useState(false)
  const [
    showIdentityMaskingConfirmDialog,
    setShowIdentityMaskingConfirmDialog,
  ] = useState(false)
  const [isUpdatingIdentityMasking, setIsUpdatingIdentityMasking] =
    useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const [accountStatusFilter, setAccountStatusFilter] = useState<string>("all")
  const [accessStatusFilter, setAccessStatusFilter] = useState<string>("all")
  const [channelTypeFilter, setChannelTypeFilter] = useState<string>("all")
  const [joinSourceFilter, setJoinSourceFilter] = useState<string>("all")
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([])
  const [conversationFilter, setConversationFilter] = useState<string>("all")
  const [replyModeAgentId, setReplyModeAgentId] = useState("")
  const [allMatchingSelected, setAllMatchingSelected] = useState(false)
  const [replyModeAgentsLoadFailed, setReplyModeAgentsLoadFailed] =
    useState(false)
  const [tagFilterOpen, setTagFilterOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>("created_at")
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc")
  const [showAddUserDialog, setShowAddUserDialog] = useState(false)
  const [addUserExternalUserId, setAddUserExternalUserId] = useState("")
  const [addUserEmail, setAddUserEmail] = useState("")
  const [addUserName, setAddUserName] = useState("")
  const [addUserPhone, setAddUserPhone] = useState("")
  const [addUserCompany, setAddUserCompany] = useState("")
  const [addUserJobTitle, setAddUserJobTitle] = useState("")
  const [addUserNote, setAddUserNote] = useState("")
  const [addUserChannelValue, setAddUserChannelValue] = useState("")
  const [addUserSubmitting, setAddUserSubmitting] = useState(false)
  const [showCsvDialog, setShowCsvDialog] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPreview, setCsvPreview] =
    useState<ClientUserCsvPreviewResponse | null>(null)
  const [csvChannelValue, setCsvChannelValue] = useState("")
  const [csvPreviewLoading, setCsvPreviewLoading] = useState(false)
  const [csvImportSubmitting, setCsvImportSubmitting] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [userToRemove, setUserToRemove] = useState<{
    id: string
    name: string | null
    email: string | null
  } | null>(null)
  const [selectedClientUserIds, setSelectedClientUserIds] = useState<
    Set<string>
  >(new Set())
  const [bulkActionLoading, setBulkActionLoading] = useState(false)
  const [statusUpdatingUserIds, setStatusUpdatingUserIds] = useState<
    Set<string>
  >(new Set())
  const [showBulkRemoveDialog, setShowBulkRemoveDialog] = useState(false)
  const [clientUserTags, setClientUserTags] = useState<ClientUserTag[]>([])
  const [showTagManagerDialog, setShowTagManagerDialog] = useState(false)
  const [tagActionLoading, setTagActionLoading] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<ClientUserTag | null>(null)
  const [tagUpdatingUserIds, setTagUpdatingUserIds] = useState<Set<string>>(
    new Set()
  )
  const [openTagEditorUserId, setOpenTagEditorUserId] = useState<string | null>(
    null
  )
  const [creditCapInput, setCreditCapInput] = useState(
    workspace.monthlyEndUserCreditCap?.toString() ?? ""
  )
  const [creditCapMessageInput, setCreditCapMessageInput] = useState(
    getCreditCapMessageInput(workspace.endUserCreditCapBlockMessageI18n)
  )
  const [creditCapError, setCreditCapError] = useState<string | null>(null)
  const [isSavingExternalUserSettings, setIsSavingExternalUserSettings] =
    useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  const [contactEmailInput, setContactEmailInput] = useState(
    getContactEmailInput(workspace)
  )
  const [contactEmailError, setContactEmailError] = useState<string | null>(
    null
  )
  const [clientChannelsLoaded, setClientChannelsLoaded] = useState(false)
  const [clientChannelsLoadFailed, setClientChannelsLoadFailed] =
    useState(false)
  const selectAllRef = useRef<HTMLInputElement | null>(null)

  const itemsPerPage = 20
  const isOrgPrivileged =
    currentOrganization?.myRole === OrganizationRole.Owner ||
    currentOrganization?.myRole === OrganizationRole.Admin
  const canToggleAccessMode =
    isOrgPrivileged || workspace.myRole === WorkspaceRole.Owner
  const canManageUsers =
    isOrgPrivileged ||
    workspace.myRole === WorkspaceRole.Owner ||
    workspace.myRole === WorkspaceRole.Admin
  const isIdentitySearchDisabled = !!workspace.maskEndUserIdentity

  const { fetchFn: getClientAccess } = useGetClientAccessApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: updateWorkspace } = useUpdateWorkspacesApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: updateWorkspaceEndUserIdentityMasking } =
    useUpdateWorkspaceEndUserIdentityMaskingApi(
      workspace.organizationId || "",
      workspace.id
    )
  const { fetchFn: updateClientAccess } = useUpdateClientAccessApi(
    workspace.organizationId || "",
    workspace.id
  )
  const {
    data: listClientUsersData,
    status: listClientUsersStatus,
    fetchFn: listClientUsers,
  } = useListClientUsersApi(workspace.organizationId || "", workspace.id)
  const listClientUsersAbortControllerRef = useRef<AbortController | null>(null)
  const { fetchFn: listClientUserTags } = useListClientUserTagsApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: getClientUserTagUsageCounts } =
    useGetClientUserTagUsageCountsApi(
      workspace.organizationId || "",
      workspace.id
    )
  const { fetchFn: createClientUserTag } = useCreateClientUserTagApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: deleteClientUserTag } = useDeleteClientUserTagApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: setClientUserTagsApi } = useSetClientUserTagsApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: createClientUser } = useCreateClientUserApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: previewImportCsv } = usePreviewImportClientUsersCsvApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { fetchFn: confirmImportCsv } = useConfirmImportClientUsersCsvApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { data: clientChannelsData, fetchFn: getClientChannels } =
    useGetClientChannelsApi(workspace.organizationId || "", workspace.id)
  const { fetchFn: batchClientUsers } = useBatchClientUsersApi(
    workspace.organizationId || "",
    workspace.id
  )
  const { data: replyModeAgentsData, fetchFn: listReplyModeAgents } =
    useListReplyModeAgentsApi(workspace.organizationId || "", workspace.id)
  const replyMode = useReplyModeBatch(
    workspace.organizationId || "",
    workspace.id,
    {
      onRequestError: (error) =>
        showApiErrorToast(error, t(`${RM}.errors.request`)),
    }
  )
  const replyModeAgents = useMemo(
    () => replyModeAgentsData?.data || [],
    [replyModeAgentsData?.data]
  )
  const selectedReplyModeAgent =
    replyModeAgents.find((agent) => agent.id === replyModeAgentId) || null
  // Names seen in the list, so result rows can use the current (possibly
  // masked) display name. Batch responses never include identities.
  const knownCustomerNamesRef = useRef<Map<string, string>>(new Map())

  const configuredClientUserChannelOptions = useMemo(
    (): ClientUserChannelOption[] =>
      (clientChannelsData?.data || [])
        .filter((channel) => isClientChannelType(channel.type))
        .map((channel) => ({
          value: `channel:${channel.id}`,
          channelType: channel.type as ClientChannelType,
          clientChannelId: channel.id,
          channelName: channel.name || undefined,
          channelSlug: channel.slug,
          allowAnonymousAccess: channel.allow_anonymous_access ?? false,
        })),
    [clientChannelsData]
  )

  const clientUserChannelOptions = useMemo(() => {
    const options: ClientUserChannelOption[] = []
    for (const channelType of CLIENT_USER_CHANNEL_TYPES) {
      const configuredOptions = configuredClientUserChannelOptions.filter(
        (option) => option.channelType === channelType
      )
      if (channelType === ClientChannelType.WEB && configuredOptions.length) {
        options.push(...configuredOptions)
        continue
      }

      if (configuredOptions.length === 1) {
        options.push(configuredOptions[0])
        continue
      }

      options.push({
        value: `type:${channelType}`,
        channelType,
      })
    }
    return options
  }, [configuredClientUserChannelOptions])

  const defaultClientUserChannelValue = useMemo(
    () =>
      getDefaultClientUserChannelValue(
        clientUserChannelOptions,
        configuredClientUserChannelOptions,
        clientChannelsLoaded
      ),
    [
      clientUserChannelOptions,
      configuredClientUserChannelOptions,
      clientChannelsLoaded,
    ]
  )
  const selectedAddUserChannel = clientUserChannelOptions.find(
    (option) => option.value === addUserChannelValue
  )
  const selectedCsvChannel = clientUserChannelOptions.find(
    (option) => option.value === csvChannelValue
  )
  const addUserChannelType = selectedAddUserChannel?.channelType || ""
  const csvChannelType = selectedCsvChannel?.channelType || ""
  const configuredWebChannelOptions = configuredClientUserChannelOptions.filter(
    (option) => option.channelType === ClientChannelType.WEB
  )
  const allConfiguredWebChannelsRequireGoogleLogin =
    configuredWebChannelOptions.length > 0 &&
    configuredWebChannelOptions.every(
      (option) => option.allowAnonymousAccess === false
    )
  const hasMixedWebChannelIdentityModes =
    configuredWebChannelOptions.some(
      (option) => option.allowAnonymousAccess === true
    ) &&
    configuredWebChannelOptions.some(
      (option) => option.allowAnonymousAccess === false
    )
  const usesGoogleLoginEmail = requiresGoogleLoginEmail(selectedAddUserChannel)
  const usesGoogleLoginEmailForCsv =
    requiresGoogleLoginEmail(selectedCsvChannel)
  const identityConfigurationLoadFailed =
    accessModeLoadFailed || clientChannelsLoadFailed
  const normalizedAddUserEmail = addUserEmail.trim().toLowerCase()
  const addUserLoginIdentity = usesGoogleLoginEmail
    ? normalizedAddUserEmail
    : addUserExternalUserId.trim()
  const addUserIdentityField = getChannelIdentityField(addUserChannelType)
  const csvIdentityField = usesGoogleLoginEmailForCsv
    ? CSV_GOOGLE_LOGIN_IDENTITY_FIELD
    : getChannelIdentityField(csvChannelType)
  const csvExampleRow = `${csvIdentityField.example},${
    usesGoogleLoginEmailForCsv ? "" : "john@example.com"
  },John Doe,0912345678,ABC Corp,Product Manager,VIP`

  useEffect(() => {
    if (!workspace.organizationId) return
    setSelectedClientUserIds(new Set())
    setAllMatchingSelected(false)
    setReplyModeAgentId("")
    replyMode.reset()
    knownCustomerNamesRef.current = new Map()
    setCurrentPage(1)
    setSearchQuery("")
    setTagFilterIds([])
    setTagFilterOpen(false)
    setAccessModeLoaded(false)
    void loadAccessMode()
    void loadClientChannels()
    void loadClientUserTags()
    void loadReplyModeAgents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.organizationId, workspace.id])

  useEffect(
    () => () => {
      listClientUsersAbortControllerRef.current?.abort()
    },
    [workspace.organizationId, workspace.id]
  )

  useEffect(() => {
    if (!isIdentitySearchDisabled || !searchQuery) return
    setSearchQuery("")
    setCurrentPage(1)
  }, [isIdentitySearchDisabled, searchQuery])

  useEffect(() => {
    setAddUserChannelValue(defaultClientUserChannelValue)
    setCsvChannelValue(defaultClientUserChannelValue)
  }, [defaultClientUserChannelValue])

  useEffect(() => {
    setCreditCapInput(workspace.monthlyEndUserCreditCap?.toString() ?? "")
    setCreditCapMessageInput(
      getCreditCapMessageInput(workspace.endUserCreditCapBlockMessageI18n)
    )
    setCreditCapError(null)
    setContactEmailInput(workspace.contactEmail || "")
    setContactEmailError(null)
  }, [
    workspace.id,
    workspace.contactEmail,
    workspace.monthlyEndUserCreditCap,
    workspace.endUserCreditCapBlockMessageI18n,
  ])

  useEffect(() => {
    if (!workspace.organizationId) return
    void loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearchQuery,
    accountStatusFilter,
    accessStatusFilter,
    channelTypeFilter,
    joinSourceFilter,
    tagFilterIds,
    conversationFilter,
    sortField,
    sortOrder,
    workspace.organizationId,
    workspace.id,
  ])

  // Page-only selection is cleared on page or sort changes. An all-matching
  // snapshot is a fixed list, so it stays selected across pagination.
  useEffect(() => {
    setSelectedClientUserIds(new Set())
    if (replyMode.source === "ids") replyMode.resetIfNotStarted()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortField, sortOrder])

  // Refresh list summaries once a reply mode run settles.
  const previousReplyModePhaseRef = useRef(replyMode.phase)
  useEffect(() => {
    const previous = previousReplyModePhaseRef.current
    previousReplyModePhaseRef.current = replyMode.phase
    if (
      replyMode.phase === "ready" &&
      (previous === "executing" || previous === "reconciling")
    ) {
      void loadUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyMode.phase])

  // Filter or agent changes clear every selection and drop any snapshot that
  // has not been executed yet.
  useEffect(() => {
    setSelectedClientUserIds(new Set())
    setAllMatchingSelected(false)
    replyMode.resetIfNotStarted()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchQuery,
    accountStatusFilter,
    accessStatusFilter,
    channelTypeFilter,
    joinSourceFilter,
    tagFilterIds,
    conversationFilter,
    replyModeAgentId,
  ])

  const showApiErrorToast = (
    error: unknown,
    fallbackMessage: string,
    getFallbackMessage?: (
      errorData: ApiErrorData | null,
      error: HTTPError
    ) => string
  ) => {
    const errorData = getApiErrorData(error)
    const limitKey = getLimitErrorTranslationKey(errorData?.error_code)
    const limitMessage = limitKey ? t(limitKey) : null
    let descriptionFallback = fallbackMessage
    if (error instanceof HTTPError && getFallbackMessage) {
      const customFallback = getFallbackMessage(errorData, error)
      if (customFallback) descriptionFallback = customFallback
    }

    toast({
      title: fallbackMessage,
      description:
        limitMessage ??
        getUserFacingApiErrorMessage(error, t, descriptionFallback),
      ...getApiErrorPresentationProps(error),
    })
  }

  const loadAccessMode = async () => {
    setAccessModeLoaded(false)
    setAccessModeLoadFailed(false)
    try {
      const result = await getClientAccess()
      if (!result?.data) {
        setAccessModeLoadFailed(true)
        toast({
          title: t("crm.userManagement.errors.loadMode"),
          variant: "destructive",
        })
        return
      }
      setAccessMode(result.data.mode)
      setAccessModeLoaded(true)
    } catch (error) {
      setAccessModeLoadFailed(true)
      showApiErrorToast(error, t("crm.userManagement.errors.loadMode"))
    }
  }

  const loadClientChannels = async () => {
    setClientChannelsLoaded(false)
    setClientChannelsLoadFailed(false)
    try {
      const result = await getClientChannels()
      if (!result?.data) {
        setClientChannelsLoadFailed(true)
        toast({
          title: t("crm.userManagement.errors.loadChannels"),
          variant: "destructive",
        })
        return
      }
      setClientChannelsLoaded(true)
    } catch (error) {
      setClientChannelsLoadFailed(true)
      showApiErrorToast(error, t("crm.userManagement.errors.loadChannels"))
    }
  }

  const retryIdentityConfigurationLoad = () => {
    if (accessModeLoadFailed) void loadAccessMode()
    if (clientChannelsLoadFailed) void loadClientChannels()
  }

  const loadReplyModeAgents = async () => {
    if (!workspace.organizationId) return
    setReplyModeAgentsLoadFailed(false)
    try {
      const result = await listReplyModeAgents()
      if (!result?.data) setReplyModeAgentsLoadFailed(true)
    } catch {
      setReplyModeAgentsLoadFailed(true)
    }
  }

  const loadClientUserTags = async () => {
    if (!workspace.organizationId) return
    try {
      const result = await listClientUserTags()
      setClientUserTags(result?.data || [])
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.loadTags"))
    }
  }

  const refreshClientUserTagCounts = async (tagIds: string[]) => {
    if (!workspace.organizationId) return
    const uniqueTagIds = Array.from(new Set(tagIds)).filter(Boolean)
    if (uniqueTagIds.length === 0) return

    const result = await getClientUserTagUsageCounts({
      jsonBody: { tag_ids: uniqueTagIds },
    })
    const countsByTagId = new Map(
      (result?.data || []).map((item) => [item.tag_id, item.client_user_count])
    )
    if (countsByTagId.size === 0) return

    setClientUserTags((currentTags) =>
      currentTags.map((tag) => {
        const nextCount = countsByTagId.get(tag.id)
        return nextCount === undefined
          ? tag
          : { ...tag, client_user_count: nextCount }
      })
    )
    setTagToDelete((currentTag) => {
      if (!currentTag) return currentTag
      const nextCount = countsByTagId.get(currentTag.id)
      return nextCount === undefined
        ? currentTag
        : { ...currentTag, client_user_count: nextCount }
    })
  }

  // Listing and frozen selection must use exactly the same customer filters.
  const buildClientUserFilters = (
    omitSearch = false
  ): ReplyModeBatchFilters => {
    const filters: ReplyModeBatchFilters = {}
    if (!omitSearch && !isIdentitySearchDisabled && debouncedSearchQuery)
      filters.q = debouncedSearchQuery
    if (accountStatusFilter !== "all")
      filters.account_status = accountStatusFilter
    if (accessStatusFilter !== "all") filters.access_status = accessStatusFilter
    if (channelTypeFilter !== "all") filters.channel_type = channelTypeFilter
    if (joinSourceFilter !== "all") filters.join_source = joinSourceFilter
    if (conversationFilter !== "all")
      filters.has_conversation = conversationFilter === "true"
    if (tagFilterIds.length > 0) filters.tag_ids = [...tagFilterIds]
    return filters
  }

  const loadUsers = async (options?: { omitSearch?: boolean }) => {
    if (!workspace.organizationId) return

    listClientUsersAbortControllerRef.current?.abort()
    const abortController = new AbortController()
    listClientUsersAbortControllerRef.current = abortController

    try {
      const params = new URLSearchParams()
      params.set("limit", itemsPerPage.toString())
      params.set("offset", ((currentPage - 1) * itemsPerPage).toString())
      params.set("order_by", sortOrder === "desc" ? `-${sortField}` : sortField)

      for (const [key, value] of Object.entries(
        buildClientUserFilters(options?.omitSearch)
      )) {
        for (const entry of Array.isArray(value) ? value : [value]) {
          params.append(key, String(entry))
        }
      }

      await listClientUsers({
        searchParams: params,
        signal: abortController.signal,
      })
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.loadUsers"))
    } finally {
      if (listClientUsersAbortControllerRef.current === abortController) {
        listClientUsersAbortControllerRef.current = null
      }
    }
  }

  const handleIdentityMaskingSwitchChange = (checked: boolean) => {
    if (!checked || !canManageUsers || workspace.maskEndUserIdentity) return
    setShowIdentityMaskingConfirmDialog(true)
  }

  const handleConfirmIdentityMasking = async () => {
    if (!canManageUsers || workspace.maskEndUserIdentity) return
    try {
      setIsUpdatingIdentityMasking(true)
      const result = await updateWorkspaceEndUserIdentityMasking({
        jsonBody: { enabled: true },
      })
      if (result?.data) {
        onWorkspaceUpdated?.(workspaceResponseToWorkspace(result.data))
      }
      toast({ title: t("crm.userManagement.identityMasking.messages.enabled") })
      setShowIdentityMaskingConfirmDialog(false)
      setShowAddUserDialog(false)
      setShowCsvDialog(false)
      setSearchQuery("")
      await loadUsers({ omitSearch: true })
    } catch (error) {
      showApiErrorToast(
        error,
        t("crm.userManagement.identityMasking.errors.enable")
      )
    } finally {
      setIsUpdatingIdentityMasking(false)
    }
  }

  const handleAccessModeChange = async (mode: ClientAccessMode) => {
    if (!canToggleAccessMode) return
    if (accessMode === mode) return
    if (accessMode === "open" && mode === "whitelist") {
      setPendingAccessMode(mode)
      setShowAccessModeConfirmDialog(true)
      return
    }
    if (accessMode === "whitelist" && mode === "open") {
      setPendingAccessMode(mode)
      setShowAccessModeOpenWarningDialog(true)
      return
    }
    try {
      await updateClientAccess({ jsonBody: { mode } })
      setAccessMode(mode)
      toast({ title: t("crm.userManagement.messages.modeUpdated") })
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.updateMode"))
    }
  }

  const handleConfirmAccessModeChange = async () => {
    if (!canToggleAccessMode) return
    if (!pendingAccessMode) return
    try {
      await updateClientAccess({ jsonBody: { mode: pendingAccessMode } })
      setAccessMode(pendingAccessMode)
      toast({ title: t("crm.userManagement.messages.modeUpdated") })
      setShowAccessModeConfirmDialog(false)
      setPendingAccessMode(null)
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.updateMode"))
    }
  }

  const handleCreateSingleUser = async () => {
    if (!canManageUsers) return
    const email = normalizedAddUserEmail
    const name = addUserName.trim()
    if (!addUserLoginIdentity || !name || !addUserChannelType) return
    try {
      setAddUserSubmitting(true)
      const result = await createClientUser({
        jsonBody: {
          external_user_id: addUserLoginIdentity,
          channel_type: addUserChannelType,
          ...(selectedAddUserChannel?.clientChannelId
            ? { client_channel_id: selectedAddUserChannel.clientChannelId }
            : {}),
          email: email || undefined,
          name,
          phone: addUserPhone.trim() || undefined,
          company: addUserCompany.trim() || undefined,
          job_title: addUserJobTitle.trim() || undefined,
          note: addUserNote.trim() || undefined,
        },
      })
      if (!result?.data) throw new Error("no data")
      toast({
        title: t("crm.userManagement.messages.usersAdded", {
          added: 1,
          skipped: 0,
        }),
      })
      setShowAddUserDialog(false)
      setAddUserExternalUserId("")
      setAddUserEmail("")
      setAddUserName("")
      setAddUserPhone("")
      setAddUserCompany("")
      setAddUserJobTitle("")
      setAddUserNote("")
      setAddUserChannelValue(defaultClientUserChannelValue)
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.addUsers"))
    } finally {
      setAddUserSubmitting(false)
    }
  }

  const handleAddUserChannelChange = (nextChannelValue: string) => {
    const nextChannel = clientUserChannelOptions.find(
      (option) => option.value === nextChannelValue
    )
    if (requiresGoogleLoginEmail(nextChannel) !== usesGoogleLoginEmail) {
      setAddUserEmail("")
    }
    setAddUserChannelValue(nextChannelValue)
  }

  const buildCsvFormData = (file: File | null, channelType: string) => {
    if (!file) return null
    if (!channelType) return null
    const formData = new FormData()
    formData.append("file", file)
    return formData
  }

  const handlePreviewCsv = async (file: File | null) => {
    if (!canManageUsers) return
    const formData = buildCsvFormData(file, csvChannelType)
    if (!formData) return

    try {
      setCsvPreviewLoading(true)
      const searchParams = new URLSearchParams({
        dry_run: "1",
        channel_type: csvChannelType,
      })
      if (selectedCsvChannel?.clientChannelId) {
        searchParams.set(
          "client_channel_id",
          selectedCsvChannel.clientChannelId
        )
      }
      const result = await previewImportCsv({
        body: formData,
        searchParams,
      })
      setCsvPreview(result?.data || null)
    } catch (error) {
      setCsvPreview(null)
      showApiErrorToast(error, t("crm.userManagement.errors.importCsv"))
    } finally {
      setCsvPreviewLoading(false)
    }
  }

  const translateCsvError = (error: string | null | undefined): string => {
    if (!error) return t("crm.userManagement.addUsers.csvImport.error")

    const externalUserIdMaxLengthMatch = error.match(
      /^External User ID must be at most (\d+) characters$/
    )
    if (externalUserIdMaxLengthMatch) {
      return t(
        "crm.userManagement.addUsers.csvImport.errors.externalUserIdMaxLength",
        {
          max: externalUserIdMaxLengthMatch[1],
        }
      )
    }

    const errorMap: Record<string, string> = {
      "External User ID is required": t(
        "crm.userManagement.addUsers.csvImport.errors.externalUserIdRequired"
      ),
      "Name is required": t(
        "crm.userManagement.addUsers.csvImport.errors.nameRequired"
      ),
      "Duplicate external user ID in CSV": t(
        "crm.userManagement.addUsers.csvImport.errors.duplicateExternalUserIdInCsv"
      ),
      "Duplicate external user ID in workspace": t(
        "crm.userManagement.addUsers.csvImport.errors.duplicateExternalUserIdInWorkspace"
      ),
      "Invalid phone number": t(
        "crm.userManagement.addUsers.csvImport.errors.invalidPhoneNumber"
      ),
      "External User ID must be a valid email for this Web client": t(
        "crm.userManagement.addUsers.csvImport.errors.webWhitelistExternalUserIdEmail"
      ),
      "External User ID must be a valid email for Browser Extension clients": t(
        "crm.userManagement.addUsers.csvImport.errors.browserExtensionExternalUserIdEmail"
      ),
      "External User ID must be a valid LINE User ID": t(
        "crm.userManagement.addUsers.csvImport.errors.lineExternalUserId"
      ),
      "External User ID must be a valid email or Slack Member ID": t(
        "crm.userManagement.addUsers.csvImport.errors.slackExternalUserId"
      ),
      "External User ID must be a valid Messenger Sender ID": t(
        "crm.userManagement.addUsers.csvImport.errors.messengerExternalUserId"
      ),
      "External User ID must be a valid Instagram User ID": t(
        "crm.userManagement.addUsers.csvImport.errors.instagramExternalUserId"
      ),
    }

    return errorMap[error] || error
  }

  const handleFilesSelected = async (files: File[]) => {
    if (!canManageUsers) return
    const csvFile = files.find((f) => f.name.endsWith(".csv"))
    if (!csvFile) {
      toast({
        title: t("crm.userManagement.errors.importCsv"),
        description: t("crm.userManagement.addUsers.csvImport.invalidFileType"),
        variant: "destructive",
      })
      return
    }
    setCsvPreview(null)
    setCsvFile(csvFile)
    await handlePreviewCsv(csvFile)
  }

  const handleConfirmCsv = async () => {
    if (!canManageUsers) return
    const formData = buildCsvFormData(csvFile, csvChannelType)
    if (!formData) return

    try {
      setCsvImportSubmitting(true)
      const searchParams = new URLSearchParams({
        channel_type: csvChannelType,
      })
      if (selectedCsvChannel?.clientChannelId) {
        searchParams.set(
          "client_channel_id",
          selectedCsvChannel.clientChannelId
        )
      }
      const result = await confirmImportCsv({
        body: formData,
        searchParams,
      })
      const data = result?.data
      if (!data) throw new Error("no data")
      toast({
        title: t("crm.userManagement.messages.csvImported", {
          added: data.success,
          skipped: data.failed,
        }),
      })
      setShowCsvDialog(false)
      setCsvFile(null)
      setCsvPreview(null)
      await loadUsers()
    } catch (error) {
      showApiErrorToast(
        error,
        t("crm.userManagement.errors.importCsv"),
        (_errorData, httpError) =>
          httpError.response.status === 429
            ? t("crm.userManagement.errors.rateLimitReached")
            : t("crm.userManagement.errors.importCsv")
      )
    } finally {
      setCsvImportSubmitting(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortOrder("desc")
    }
  }

  const handleUserClick = (userId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }
    navigate(`/workspaces/${workspace.id}/users/${userId}`)
  }

  const handleToggleStatus = async (
    userId: string,
    currentStatus: string,
    e?: React.MouseEvent
  ) => {
    e?.stopPropagation()
    if (!canManageUsers) return
    if (statusUpdatingUserIds.has(userId)) return
    const newStatus = currentStatus === "active" ? "blocked" : "active"
    try {
      setStatusUpdatingUserIds((current) => new Set(current).add(userId))
      await ky.patch(
        `${config.serverRoot}${API_PREFIX}/organizations/${workspace.organizationId}/workspaces/${workspace.id}/client-users/${userId}/status`,
        {
          json: { status: newStatus },
          credentials: "include",
        }
      )
      toast({
        title: t("crm.userManagement.messages.statusUpdated"),
      })
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.updateStatus"))
    } finally {
      setStatusUpdatingUserIds((current) => {
        const next = new Set(current)
        next.delete(userId)
        return next
      })
    }
  }

  const handleRemoveUserClick = (
    userId: string,
    userName: string | null,
    userEmail: string | null,
    e: React.MouseEvent
  ) => {
    e.stopPropagation()
    if (!canManageUsers) return
    setUserToRemove({ id: userId, name: userName, email: userEmail })
    setShowRemoveDialog(true)
  }

  const handleConfirmRemoveUser = async () => {
    if (!userToRemove || !canManageUsers) return
    try {
      await ky.delete(
        `${config.serverRoot}${API_PREFIX}/organizations/${workspace.organizationId}/workspaces/${workspace.id}/client-users/${userToRemove.id}`,
        {
          credentials: "include",
        }
      )
      toast({
        title: t("crm.userManagement.messages.userRemoved"),
      })
      setShowRemoveDialog(false)
      setUserToRemove(null)
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.removeUser"))
    }
  }

  const handleCreateClientUserTag = async (name: string, color: string) => {
    if (!canManageUsers) return false
    const trimmedName = name.trim()
    if (!trimmedName) return false
    try {
      setTagActionLoading(true)
      await createClientUserTag({
        jsonBody: {
          name: trimmedName,
          color: color.trim() || undefined,
        },
      })
      toast({ title: t("crm.userManagement.tags.messages.created") })
      await loadClientUserTags()
      return true
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.tags.errors.create"))
      return false
    } finally {
      setTagActionLoading(false)
    }
  }

  const handleDeleteClientUserTag = async () => {
    if (!canManageUsers) return
    if (!tagToDelete) return
    try {
      setTagActionLoading(true)
      const tagId = tagToDelete.id
      await deleteClientUserTag({ urlBuilderParams: { tagId } })
      if (tagFilterIds.includes(tagId)) {
        setTagFilterIds((current) => current.filter((id) => id !== tagId))
        setCurrentPage(1)
      }
      setTagToDelete(null)
      toast({ title: t("crm.userManagement.tags.messages.deleted") })
      await loadClientUserTags()
      if (!tagFilterIds.includes(tagId)) {
        await loadUsers()
      }
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.tags.errors.delete"))
    } finally {
      setTagActionLoading(false)
    }
  }

  const handlePrepareDeleteClientUserTag = (tag: ClientUserTag) => {
    setTagToDelete(tag)
    refreshClientUserTagCounts([tag.id]).catch((error) => {
      showApiErrorToast(error, t("crm.userManagement.errors.loadTags"))
    })
  }

  const updateUserTags = async (
    user: ClientUserListItem,
    nextTagIds: string[]
  ) => {
    if (!canManageUsers) return
    const previousTagIds = user.tags.map((tag) => tag.id)
    const affectedTagIds = [...previousTagIds, ...nextTagIds]
    try {
      setTagUpdatingUserIds((current) => new Set(current).add(user.id))
      await setClientUserTagsApi({
        urlBuilderParams: { clientUserId: user.id },
        jsonBody: { tag_ids: nextTagIds },
      })
      await refreshClientUserTagCounts(affectedTagIds)
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.tags.errors.assign"))
    } finally {
      setTagUpdatingUserIds((current) => {
        const next = new Set(current)
        next.delete(user.id)
        return next
      })
    }
  }

  const handleAddUserTag = (user: ClientUserListItem, tagId: string) => {
    if (user.tags.some((tag) => tag.id === tagId)) return
    void updateUserTags(user, [...user.tags.map((tag) => tag.id), tagId])
  }

  const handleRemoveUserTag = (user: ClientUserListItem, tagId: string) => {
    void updateUserTags(
      user,
      user.tags.map((tag) => tag.id).filter((id) => id !== tagId)
    )
  }

  const handleTagEditorOpenChange = useCallback(
    (userId: string, open: boolean) => {
      setOpenTagEditorUserId(open ? userId : null)
    },
    []
  )

  const users = useMemo(
    () => listClientUsersData?.data || [],
    [listClientUsersData?.data]
  )
  const pagination = listClientUsersData?.pagination
  const totalPages = pagination?.total_pages || 1
  const totalUsers = pagination?.total_records || 0
  const selectedOnPageCount = useMemo(
    () => users.filter((u) => selectedClientUserIds.has(u.id)).length,
    [users, selectedClientUserIds]
  )
  const allMatchingBatch =
    allMatchingSelected &&
    replyMode.source === "filters" &&
    replyMode.batch &&
    !replyMode.requestedMode
      ? replyMode.batch
      : null
  const [selectedPage, setSelectedPage] = useState<{
    key: string
    ids: Set<string>
  } | null>(null)
  const selectionPageKey = allMatchingBatch
    ? `${allMatchingBatch.id}:${users.map((user) => user.id).join(",")}`
    : ""
  const { getSelectedPageIds } = replyMode
  useEffect(() => {
    if (!allMatchingBatch || users.length === 0) return
    let stale = false
    void getSelectedPageIds(
      allMatchingBatch.id,
      users.map((user) => user.id)
    )
      .then((ids) => {
        if (!stale)
          setSelectedPage({ key: selectionPageKey, ids: new Set(ids) })
      })
      .catch((error) => {
        if (!stale) showApiErrorToast(error, t(`${RM}.errors.request`))
      })
    return () => {
      stale = true
    }
    // The key changes with the fixed snapshot and the visible customer IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionPageKey, getSelectedPageIds])
  const allMatchingIds =
    selectedPage?.key === selectionPageKey
      ? selectedPage.ids
      : new Set<string>()
  const isUserChecked = (clientUserId: string) =>
    allMatchingBatch
      ? allMatchingIds.has(clientUserId)
      : selectedClientUserIds.has(clientUserId)
  const checkedOnPageCount = allMatchingBatch
    ? users.filter((u) => allMatchingIds.has(u.id)).length
    : selectedOnPageCount
  const allOnPageSelected =
    users.length > 0 && checkedOnPageCount === users.length
  const someOnPageSelected = checkedOnPageCount > 0 && !allOnPageSelected
  const isCreatingAllMatching =
    replyMode.source === "filters" &&
    (replyMode.phase === "creating" || replyMode.phase === "createUnknown")
  const hasSelection =
    selectedClientUserIds.size > 0 ||
    !!allMatchingBatch ||
    isCreatingAllMatching
  const selectionCount = allMatchingBatch
    ? allMatchingBatch.total
    : selectedClientUserIds.size
  const selectionHint = allMatchingBatch
    ? t(`${RM}.selection.snapshotAt`, {
        time: new Date(allMatchingBatch.snapshot_at).toLocaleString(),
      })
    : isCreatingAllMatching && replyMode.phase === "creating"
      ? t(`${RM}.selection.creatingSnapshot`)
      : t(`${RM}.selection.pageOnly`)
  const bulkAccountActions = [
    {
      key: "enable",
      variant: "outline",
      onClick: () => handleBulkStatusUpdate("active"),
    },
    {
      key: "disable",
      variant: "outline",
      onClick: () => handleBulkStatusUpdate("blocked"),
    },
    {
      key: "remove",
      variant: "destructive",
      onClick: () => setShowBulkRemoveDialog(true),
    },
  ] as const

  // Check if this is the initial empty state (no users and no filters applied)
  const isInitialEmptyState =
    users.length === 0 &&
    listClientUsersStatus !== FetchStatus.Loading &&
    !debouncedSearchQuery &&
    accountStatusFilter === "all" &&
    accessStatusFilter === "all" &&
    channelTypeFilter === "all" &&
    joinSourceFilter === "all" &&
    conversationFilter === "all" &&
    tagFilterIds.length === 0
  useEffect(() => {
    if (!selectAllRef.current) return
    selectAllRef.current.indeterminate = someOnPageSelected
  }, [someOnPageSelected])

  useEffect(() => {
    for (const user of users) {
      knownCustomerNamesRef.current.set(
        user.id,
        getUserPrimaryDisplayName(user)
      )
    }
  }, [users])

  const handleReplyModeAgentChange = (agentId: string) => {
    if (replyMode.busy || agentId === replyModeAgentId) return
    setReplyModeAgentId(agentId)
  }

  const handleSelectAllMatching = async () => {
    if (!canManageUsers || !replyModeAgentId || !replyMode.canCreate) return
    const created = await replyMode.createBatch({
      agentId: replyModeAgentId,
      source: "filters",
      filters: buildClientUserFilters(),
    })
    if (!created) return
    setSelectedClientUserIds(new Set())
    setAllMatchingSelected(true)
  }

  const handlePrepareReplyMode = async (): Promise<ReplyModeBatch | null> => {
    if (!replyModeAgentId) return null
    if (allMatchingBatch) return allMatchingBatch
    const ids = Array.from(selectedClientUserIds)
    if (ids.length === 0) return null
    // Every selection edit invalidates an unstarted snapshot via resetIfNotStarted.
    if (
      replyMode.batch &&
      replyMode.source === "ids" &&
      !replyMode.requestedMode &&
      replyMode.phase === "ready"
    ) {
      return replyMode.batch
    }
    return replyMode.createBatch({
      agentId: replyModeAgentId,
      source: "ids",
      clientUserIds: ids,
    })
  }

  const handleRecheckReplyModeSnapshot = async () => {
    const result = await replyMode.recheck()
    if (!result) return null
    if (replyMode.source === "filters") {
      setSelectedClientUserIds(new Set())
      setAllMatchingSelected(true)
    }
    return result
  }

  const handleConfirmReplyMode = (mode: ReplyModeTarget) => {
    // Results take over from here; the selection is finished.
    setSelectedClientUserIds(new Set())
    setAllMatchingSelected(false)
    void replyMode.execute(mode)
  }

  const handleClearSelection = () => {
    setSelectedClientUserIds(new Set())
    setAllMatchingSelected(false)
    replyMode.resetIfNotStarted()
  }

  const getReplyModeCustomerLabel = (clientUserId: string, index: number) =>
    (!isIdentitySearchDisabled &&
      knownCustomerNamesRef.current.get(clientUserId)) ||
    t(`${RM}.results.customerFallback`, {
      number: index + 1,
      id: clientUserId.slice(0, 8),
    })

  const toggleSelectAllOnPage = (checked: boolean) => {
    if (allMatchingBatch) return
    replyMode.resetIfNotStarted()
    const next = new Set(selectedClientUserIds)
    if (checked) {
      for (const u of users) next.add(u.id)
    } else {
      for (const u of users) next.delete(u.id)
    }
    setSelectedClientUserIds(next)
  }

  const toggleSelectUser = (clientUserId: string, checked: boolean) => {
    if (allMatchingBatch) return
    replyMode.resetIfNotStarted()
    const next = new Set(selectedClientUserIds)
    if (checked) next.add(clientUserId)
    else next.delete(clientUserId)
    setSelectedClientUserIds(next)
  }

  const getChannelTypeLabel = (type: ClientChannelType) => {
    switch (type) {
      case ClientChannelType.WEB:
        return t("crm.userManagement.channelTypes.webClient")
      case ClientChannelType.WIDGET:
        return t("crm.userManagement.channelTypes.webWidget")
      case ClientChannelType.BROWSER_EXTENSION:
        return t("workspace.channels.types.browserExtensionShort")
      case ClientChannelType.SLACK:
        return t("workspace.channels.types.slackShort")
      case ClientChannelType.LINE:
        return t("workspace.channels.types.lineShort")
      case ClientChannelType.MESSENGER:
        return t("workspace.channels.types.messengerShort")
      case ClientChannelType.INSTAGRAM:
        return t("workspace.channels.types.instagramShort")
      default:
        return type
    }
  }

  const getClientUserChannelOptionLabel = (option: ClientUserChannelOption) => {
    const typeLabel = getChannelTypeLabel(option.channelType)
    const sameTypeOptionCount = clientUserChannelOptions.filter(
      (candidate) => candidate.channelType === option.channelType
    ).length
    if (sameTypeOptionCount <= 1 || !option.clientChannelId) return typeLabel
    return `${typeLabel} · ${option.channelName || option.channelSlug}`
  }

  const getStatusBadgeVariant = (status: string) => {
    if (status === "blocked") return "destructive"
    return "secondary"
  }

  const getStatusBadgeClassName = (status: string) => {
    if (status === "active")
      return "bg-green-600 text-white hover:bg-green-700 border-transparent"
    return ""
  }

  const getLastActiveAt = (user: ClientUserListItem) =>
    user.last_conversation_at || user.last_login_at

  const getUserPrimaryDisplayName = (user: ClientUserListItem) => {
    const name = user.name?.trim()
    const alias = user.alias?.trim()

    if (name && alias && name !== alias) return `${name} (${alias})`
    if (name) return name
    if (alias) return alias
    return user.display_name || user.email || user.external_user_id || "-"
  }

  const getUserIdentityLines = (
    user: ClientUserListItem
  ): UserIdentityLine[] => {
    if (workspace.maskEndUserIdentity) return []

    const externalUserId = user.external_user_id?.trim() || ""
    const email = user.email?.trim() || ""

    const emailLine = (): UserIdentityLine => ({
      labelKey: "crm.userManagement.table.email",
      value: email,
    })
    const additionalEmailLine = (
      caseSensitiveIdentity = false
    ): UserIdentityLine[] => {
      if (!email || !externalUserId) return []
      const isSameIdentity = caseSensitiveIdentity
        ? email === externalUserId
        : email.toLowerCase() === externalUserId.toLowerCase()
      return isSameIdentity ? [] : [emailLine()]
    }

    switch (user.client_channel_type) {
      case ClientChannelType.WEB: {
        const externalUserIdIsEmail = isValidEmail(externalUserId)
        const lines: UserIdentityLine[] = []
        if (externalUserId) {
          lines.push({
            labelKey: externalUserIdIsEmail
              ? "crm.userManagement.table.googleLoginEmail"
              : "crm.userManagement.table.webUserId",
            value: externalUserId,
          })
        } else if (email) {
          lines.push({
            labelKey: "crm.userManagement.table.googleLoginEmail",
            value: email,
          })
        }
        lines.push(...additionalEmailLine())
        return lines
      }
      case ClientChannelType.BROWSER_EXTENSION: {
        const lines: UserIdentityLine[] = []
        if (externalUserId) {
          lines.push({
            labelKey: isValidEmail(externalUserId)
              ? "crm.userManagement.table.googleLoginEmail"
              : "crm.userManagement.table.browserExtensionUserId",
            value: externalUserId,
          })
        } else if (email) {
          lines.push({
            labelKey: "crm.userManagement.table.googleLoginEmail",
            value: email,
          })
        }
        lines.push(...additionalEmailLine())
        return lines
      }
      case ClientChannelType.WIDGET:
        return [
          ...(externalUserId
            ? [
                {
                  labelKey: "crm.userManagement.table.websiteUserId",
                  value: externalUserId,
                },
              ]
            : []),
          ...(!externalUserId && email ? [emailLine()] : []),
          ...additionalEmailLine(),
        ]
      case ClientChannelType.SLACK: {
        const lines: UserIdentityLine[] = []
        if (externalUserId) {
          lines.push({
            labelKey: externalUserId.includes("@")
              ? "crm.userManagement.table.slackEmail"
              : "crm.userManagement.table.slackMemberId",
            value: externalUserId,
          })
        } else if (email) {
          lines.push({
            labelKey: "crm.userManagement.table.slackEmail",
            value: email,
          })
        }
        lines.push(...additionalEmailLine())
        return lines
      }
      case ClientChannelType.LINE:
      case ClientChannelType.MESSENGER:
      case ClientChannelType.INSTAGRAM: {
        const platformLabelKey = {
          [ClientChannelType.LINE]: "crm.userManagement.table.lineUserId",
          [ClientChannelType.MESSENGER]:
            "crm.userManagement.table.messengerSenderId",
          [ClientChannelType.INSTAGRAM]:
            "crm.userManagement.table.instagramUserId",
        }[user.client_channel_type]
        return [
          ...(externalUserId
            ? [{ labelKey: platformLabelKey, value: externalUserId }]
            : []),
          ...(!externalUserId && email ? [emailLine()] : []),
          ...additionalEmailLine(true),
        ]
      }
      default:
        return [
          ...(externalUserId
            ? [
                {
                  labelKey: "crm.userManagement.table.userId",
                  value: externalUserId,
                },
              ]
            : []),
          ...(!externalUserId && email ? [emailLine()] : []),
          ...additionalEmailLine(),
        ]
    }
  }

  const hasInvalidGoogleLoginIdentity = (user: ClientUserListItem) => {
    if (
      workspace.maskEndUserIdentity ||
      (user.client_channel_type !== ClientChannelType.BROWSER_EXTENSION &&
        !(
          allConfiguredWebChannelsRequireGoogleLogin &&
          user.client_channel_type === ClientChannelType.WEB
        ))
    ) {
      return false
    }

    const externalUserId = user.external_user_id?.trim() || ""
    return !isValidEmail(externalUserId)
  }

  const hasAmbiguousWebLoginIdentity = (user: ClientUserListItem) => {
    if (
      workspace.maskEndUserIdentity ||
      !hasMixedWebChannelIdentityModes ||
      user.client_channel_type !== ClientChannelType.WEB
    ) {
      return false
    }

    const externalUserId = user.external_user_id?.trim() || ""
    return !isValidEmail(externalUserId)
  }

  const getAccessToggleLabel = (user: ClientUserListItem) => {
    if (accessMode === "whitelist") {
      return user.status === "active"
        ? t("crm.userManagement.actions.disable")
        : t("crm.userManagement.actions.enable")
    }
    return user.status === "active"
      ? t("crm.userManagement.actions.block")
      : t("crm.userManagement.actions.unblock")
  }

  const getInteractionSummaryBadgeVariant = (user: ClientUserListItem) => {
    if (user.human_agent_count > 0) return "default"
    return "secondary"
  }

  const getInteractionSummaryBadgeClassName = (user: ClientUserListItem) => {
    if (user.human_agent_count > 0)
      return "bg-amber-500 text-white hover:bg-amber-600 border-transparent"
    return ""
  }

  const getInteractionSummaryLabel = (user: ClientUserListItem) => {
    if (user.human_agent_count > 0) {
      return t("crm.userManagement.interactionSummary.humanOnAgents", {
        count: user.human_agent_count,
      })
    }
    return t("crm.userManagement.interactionSummary.allAi")
  }

  const getInteractionSummaryDescription = (user: ClientUserListItem) => {
    if (user.human_manual_agent_count > 0) {
      return t("crm.userManagement.interactionSummary.manualAgents", {
        count: user.human_manual_agent_count,
      })
    }
    return null
  }

  const handleBulkStatusUpdate = async (status: "active" | "blocked") => {
    if (!canManageUsers) return
    const clientUserIds = Array.from(selectedClientUserIds)
    if (clientUserIds.length === 0) return
    try {
      setBulkActionLoading(true)
      const result = await batchClientUsers({
        jsonBody: {
          client_user_ids: clientUserIds,
          action: status === "active" ? "enable" : "disable",
        },
      })
      const data = result?.data
      toast({
        title: t("crm.userManagement.messages.bulkStatusUpdated", {
          count: data?.success ?? clientUserIds.length,
          status:
            status === "active"
              ? t("crm.userManagement.filters.active")
              : t("crm.userManagement.filters.blocked"),
        }),
      })
      setSelectedClientUserIds(new Set())
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.bulkUpdateStatus"))
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleConfirmBulkRemoveUsers = async () => {
    if (!canManageUsers) return
    const clientUserIds = Array.from(selectedClientUserIds)
    if (clientUserIds.length === 0) return
    try {
      setBulkActionLoading(true)
      const result = await batchClientUsers({
        jsonBody: { client_user_ids: clientUserIds, action: "delete" },
      })
      const data = result?.data
      toast({
        title: t("crm.userManagement.messages.bulkUsersRemoved", {
          deleted: data?.success ?? clientUserIds.length,
          notFound: data?.not_found ?? 0,
        }),
      })
      setShowBulkRemoveDialog(false)
      setSelectedClientUserIds(new Set())
      await loadUsers()
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.errors.bulkRemoveUser"))
    } finally {
      setBulkActionLoading(false)
    }
  }

  const singleUserValidation = useMemo(
    () =>
      validateUserFields({
        externalUserId: addUserLoginIdentity,
        email: addUserEmail,
        phone: addUserPhone,
        name: addUserName,
      }),
    [addUserLoginIdentity, addUserEmail, addUserPhone, addUserName]
  )
  const isAddUserChannelIdentityValid =
    usesGoogleLoginEmail ||
    !addUserChannelType ||
    isValidClientUserIdentity(addUserExternalUserId, addUserChannelType)

  const handleSaveExternalUserSettings = async () => {
    if (!workspace.organizationId) return

    const trimmedValue = creditCapInput.trim()
    const trimmedContactEmail = contactEmailInput.trim()
    let nextCreditCap: number | null = null
    const contactEmailValidation = validateUserFields({
      email: trimmedContactEmail,
      phone: "",
    })

    if (!contactEmailValidation.isEmailValid) {
      setContactEmailError(t("crm.userManagement.contactEmail.errors.invalid"))
      return
    }

    if (trimmedValue && !/^\d+$/.test(trimmedValue)) {
      setCreditCapError(t("crm.userManagement.creditCap.errors.invalid"))
      return
    }
    if (trimmedValue) {
      const parsedValue = Number(trimmedValue)
      if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0) {
        setCreditCapError(t("crm.userManagement.creditCap.errors.invalid"))
        return
      }
      nextCreditCap = parsedValue
    }
    const nextCreditCapMessagePayload = buildCreditCapMessagePayload(
      creditCapMessageInput
    )

    try {
      setIsSavingExternalUserSettings(true)
      const response = await updateWorkspace({
        jsonBody: {
          name: workspace.name,
          contact_email: trimmedContactEmail || null,
          monthly_end_user_credit_cap: nextCreditCap,
          end_user_credit_cap_block_message_i18n: nextCreditCapMessagePayload,
        },
      })
      if (response?.data) {
        const updatedWorkspace = workspaceResponseToWorkspace(response.data)
        onWorkspaceUpdated?.(updatedWorkspace)
        setContactEmailInput(getContactEmailInput(updatedWorkspace))
      }
      setCreditCapError(null)
      setContactEmailError(null)
      setCreditCapInput(nextCreditCap === null ? "" : String(nextCreditCap))
      setCreditCapMessageInput(
        getCreditCapMessageInput(nextCreditCapMessagePayload)
      )
      toast({
        title: t("crm.userManagement.policy.messages.saved"),
      })
    } catch (error) {
      showApiErrorToast(error, t("crm.userManagement.policy.errors.save"))
    } finally {
      setIsSavingExternalUserSettings(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <PermissionButton
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            disabled={
              !!workspace.maskEndUserIdentity ||
              !accessModeLoaded ||
              !clientChannelsLoaded
            }
            aria-describedby={
              workspace.maskEndUserIdentity
                ? "crm-raw-identity-actions-disabled"
                : identityConfigurationLoadFailed
                  ? "crm-identity-configuration-load-error"
                  : undefined
            }
            onClick={() => setShowAddUserDialog(true)}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t("crm.userManagement.addUsers.add")}
          </PermissionButton>
          <PermissionButton
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            disabled={
              !!workspace.maskEndUserIdentity ||
              !accessModeLoaded ||
              !clientChannelsLoaded
            }
            aria-describedby={
              workspace.maskEndUserIdentity
                ? "crm-raw-identity-actions-disabled"
                : identityConfigurationLoadFailed
                  ? "crm-identity-configuration-load-error"
                  : undefined
            }
            variant="outline"
            onClick={() => setShowCsvDialog(true)}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("crm.userManagement.addUsers.importCsv")}
          </PermissionButton>
          <PermissionButton
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            variant="outline"
            onClick={() => setShowTagManagerDialog(true)}
          >
            <Tags className="mr-2 h-4 w-4" />
            {t("crm.userManagement.tags.manage")}
          </PermissionButton>
        </div>
        {workspace.maskEndUserIdentity && (
          <p
            id="crm-raw-identity-actions-disabled"
            className="text-xs text-muted-foreground"
          >
            {t("crm.userManagement.identityMasking.rawIdentityActionsDisabled")}
          </p>
        )}
        {identityConfigurationLoadFailed && (
          <div
            id="crm-identity-configuration-load-error"
            role="alert"
            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2"
          >
            <p className="min-w-0 flex-1 text-sm text-destructive">
              {t("crm.userManagement.errors.identityConfigurationUnavailable")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={retryIdentityConfigurationLoad}
            >
              {t("crm.userManagement.actions.retryIdentityConfiguration")}
            </Button>
          </div>
        )}
      </div>

      <section className="grid gap-3 rounded-lg border p-5 md:grid-cols-[240px_minmax(0,1fr)]">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">
            {t("crm.userManagement.accessMode.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {accessMode === "open"
              ? t("crm.userManagement.accessMode.modeHints.open")
              : t("crm.userManagement.accessMode.modeHints.whitelist")}
          </p>
        </div>
        <div className="space-y-2">
          <Label>{t("crm.userManagement.accessMode.mode")}</Label>
          <Select
            value={accessMode}
            onValueChange={(v) => handleAccessModeChange(v as ClientAccessMode)}
          >
            <SelectTrigger
              className="w-full max-w-48"
              disabled={
                !canToggleAccessMode ||
                !accessModeLoaded ||
                isUpdatingAccessMode
              }
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">
                {t("crm.userManagement.accessMode.modeOptions.open")}
              </SelectItem>
              <SelectItem value="whitelist">
                {t("crm.userManagement.accessMode.modeOptions.whitelist")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="rounded-lg border px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">
              {t("crm.userManagement.policy.title")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("crm.userManagement.policy.description")}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedSettings((current) => !current)}
            aria-expanded={showAdvancedSettings}
            aria-controls="crm-advanced-settings"
            className="w-fit shrink-0 gap-2"
          >
            {showAdvancedSettings ? (
              <>
                {t("crm.userManagement.policy.collapse")}
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t("crm.userManagement.policy.expand")}
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {showAdvancedSettings && (
          <div
            id="crm-advanced-settings"
            className="mt-5 space-y-8 border-t pt-5"
          >
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">
                {t("crm.userManagement.policy.groups.contactInfo")}
              </h4>
              <div className="grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <h5 className="text-sm font-medium">
                    {t("crm.userManagement.contactEmail.title")}
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.contactEmail.description")}
                  </p>
                </div>
                <div className="max-w-md space-y-2">
                  <Label htmlFor="crm-contact-email">
                    {t("crm.userManagement.contactEmail.label")}
                  </Label>
                  <Input
                    id="crm-contact-email"
                    type="email"
                    inputMode="email"
                    placeholder={
                      workspace.contactEmailDefault ||
                      workspace.owner?.email ||
                      "owner@example.com"
                    }
                    value={contactEmailInput}
                    onChange={(e) => {
                      setContactEmailInput(e.target.value)
                      setContactEmailError(null)
                    }}
                    disabled={!canManageUsers || isSavingExternalUserSettings}
                    className={contactEmailError ? "border-destructive" : ""}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.contactEmail.helper", {
                      email:
                        workspace.contactEmailDefault ||
                        workspace.owner?.email ||
                        "-",
                    })}
                  </p>
                  {contactEmailError && (
                    <p className="text-sm text-destructive">
                      {contactEmailError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-5">
              <h4 className="text-sm font-semibold">
                {t("crm.userManagement.policy.groups.privacy")}
              </h4>
              <div className="grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <h5 className="text-sm font-medium">
                    {t("crm.userManagement.identityMasking.title")}
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.identityMasking.description")}
                  </p>
                </div>
                <div className="max-w-2xl space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <Switch
                      id="crm-identity-masking"
                      checked={!!workspace.maskEndUserIdentity}
                      disabled={
                        !canManageUsers ||
                        isUpdatingIdentityMasking ||
                        !!workspace.maskEndUserIdentity
                      }
                      onCheckedChange={handleIdentityMaskingSwitchChange}
                    />
                    <Label htmlFor="crm-identity-masking">
                      {t("crm.userManagement.identityMasking.switchLabel")}
                    </Label>
                    {workspace.maskEndUserIdentity && (
                      <Badge variant="secondary" className="gap-1">
                        <Shield className="h-3.5 w-3.5" />
                        {t("crm.userManagement.identityMasking.lockedBadge")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {workspace.maskEndUserIdentity
                      ? t("crm.userManagement.identityMasking.lockedHelper")
                      : t("crm.userManagement.identityMasking.helper")}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-5">
              <h4 className="text-sm font-semibold">
                {t("crm.userManagement.policy.groups.usageLimits")}
              </h4>
              <div className="grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <h5 className="text-sm font-medium">
                    {t("crm.userManagement.creditCap.title")}
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.creditCap.description")}
                  </p>
                </div>
                <div className="max-w-md space-y-2">
                  <Label htmlFor="monthly-end-user-credit-cap">
                    {t("crm.userManagement.creditCap.label")}
                  </Label>
                  <Input
                    id="monthly-end-user-credit-cap"
                    inputMode="numeric"
                    placeholder={t("crm.userManagement.creditCap.placeholder")}
                    value={creditCapInput}
                    onChange={(e) => {
                      setCreditCapInput(e.target.value)
                      setCreditCapError(null)
                    }}
                    disabled={!canManageUsers || isSavingExternalUserSettings}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.creditCap.helper")}
                  </p>
                  {creditCapError && (
                    <p className="text-sm text-destructive">{creditCapError}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <h5 className="text-sm font-medium">
                    {t("crm.userManagement.creditCap.message.title")}
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    {t("crm.userManagement.creditCap.message.description")}
                  </p>
                </div>
                <Tabs defaultValue="en" className="max-w-2xl space-y-3">
                  <TabsList className="h-auto flex-wrap justify-start">
                    {CREDIT_CAP_MESSAGE_LOCALES.map((locale) => (
                      <TabsTrigger key={locale} value={locale}>
                        {t(
                          `crm.userManagement.creditCap.message.locales.${locale}`
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {CREDIT_CAP_MESSAGE_LOCALES.map((locale) => (
                    <TabsContent key={locale} value={locale} className="mt-0">
                      <div className="space-y-2">
                        <Textarea
                          value={creditCapMessageInput[locale]}
                          placeholder={t(
                            "crm.userManagement.creditCap.message.placeholder"
                          )}
                          onChange={(e) =>
                            setCreditCapMessageInput((current) => ({
                              ...current,
                              [locale]: e.target.value,
                            }))
                          }
                          disabled={
                            !canManageUsers || isSavingExternalUserSettings
                          }
                          className="min-h-24"
                        />
                        {!creditCapMessageInput[locale].trim() && (
                          <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {t(
                                "crm.userManagement.creditCap.message.defaultLabel"
                              )}
                            </span>{" "}
                            {workspace
                              .endUserCreditCapBlockMessageDefaultI18n?.[
                              locale
                            ] || ""}
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>

            {canManageUsers && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveExternalUserSettings}
                  disabled={isSavingExternalUserSettings}
                >
                  {t("crm.userManagement.creditCap.save")}
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="border-t" />

      {isInitialEmptyState ? (
        <OnboardingEmptyState
          icon={Users}
          title={t("crm.userManagement.onboarding.title")}
          description={t("crm.userManagement.onboarding.description")}
          steps={[
            { title: t("crm.userManagement.onboarding.steps.addUsers") },
            { title: t("crm.userManagement.onboarding.steps.setAccessMode") },
            { title: t("crm.userManagement.onboarding.steps.manageUsers") },
          ]}
          primaryAction={
            workspace.maskEndUserIdentity ||
            !accessModeLoaded ||
            !clientChannelsLoaded
              ? undefined
              : {
                  label: t("crm.userManagement.onboarding.cta"),
                  onClick: () => setShowAddUserDialog(true),
                  permission: {
                    required: Permission.WsUpdateResources,
                    workspace,
                    disabledTooltip: t("common.noPermission"),
                  },
                }
          }
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder={t(
                  isIdentitySearchDisabled
                    ? "crm.userManagement.filters.searchDisabled"
                    : "crm.userManagement.filters.search"
                )}
                value={searchQuery}
                disabled={isIdentitySearchDisabled}
                aria-describedby={
                  isIdentitySearchDisabled
                    ? "crm-user-search-disabled-helper"
                    : undefined
                }
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
              />
              {isIdentitySearchDisabled && (
                <p
                  id="crm-user-search-disabled-helper"
                  className="mt-1 text-xs text-muted-foreground"
                >
                  {t("crm.userManagement.filters.searchDisabledHelper")}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.accountStatus")}
              </Label>
              <Select
                value={accountStatusFilter}
                onValueChange={(v) => {
                  setAccountStatusFilter(v)
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("crm.userManagement.filters.all")}
                  </SelectItem>
                  <SelectItem value="pending">
                    {t("crm.userManagement.filters.pending")}
                  </SelectItem>
                  <SelectItem value="active">
                    {t("crm.userManagement.filters.active")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.accessStatus")}
              </Label>
              <Select
                value={accessStatusFilter}
                onValueChange={(v) => {
                  setAccessStatusFilter(v)
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("crm.userManagement.filters.all")}
                  </SelectItem>
                  <SelectItem value="active">
                    {t("crm.userManagement.filters.active")}
                  </SelectItem>
                  <SelectItem value="blocked">
                    {t("crm.userManagement.filters.blocked")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.channelType")}
              </Label>
              <Select
                value={channelTypeFilter}
                onValueChange={(v) => {
                  setChannelTypeFilter(v)
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("crm.userManagement.filters.all")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.WEB}>
                    {t("workspace.channels.types.webShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.WIDGET}>
                    {t("workspace.channels.types.widgetShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.BROWSER_EXTENSION}>
                    {t("workspace.channels.types.browserExtensionShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.SLACK}>
                    {t("workspace.channels.types.slackShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.LINE}>
                    {t("workspace.channels.types.lineShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.MESSENGER}>
                    {t("workspace.channels.types.messengerShort")}
                  </SelectItem>
                  <SelectItem value={ClientChannelType.INSTAGRAM}>
                    {t("workspace.channels.types.instagramShort")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.joinSource")}
              </Label>
              <Select
                value={joinSourceFilter}
                onValueChange={(v) => {
                  setJoinSourceFilter(v)
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("crm.userManagement.filters.all")}
                  </SelectItem>
                  <SelectItem value="manual">
                    {t("crm.userManagement.filters.manual")}
                  </SelectItem>
                  <SelectItem value="csv">
                    {t("crm.userManagement.filters.csv")}
                  </SelectItem>
                  <SelectItem value="auto">
                    {t("crm.userManagement.filters.autoJoin")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.conversation")}
              </Label>
              <Select
                value={conversationFilter}
                onValueChange={(v) => {
                  setConversationFilter(v)
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger
                  className="w-44"
                  aria-label={t("crm.userManagement.filters.conversation")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("crm.userManagement.filters.all")}
                  </SelectItem>
                  <SelectItem value="true">
                    {t("crm.userManagement.filters.hasConversation")}
                  </SelectItem>
                  <SelectItem value="false">
                    {t("crm.userManagement.filters.noConversation")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("crm.userManagement.filters.tag")}
              </Label>
              <ClientUserTagFilter
                tags={clientUserTags}
                selectedTagIds={tagFilterIds}
                open={tagFilterOpen}
                onOpenChange={setTagFilterOpen}
                onChange={(nextTagIds) => {
                  setTagFilterIds(nextTagIds)
                  setCurrentPage(1)
                  window.setTimeout(() => setTagFilterOpen(true), 0)
                  window.setTimeout(() => setTagFilterOpen(true), 350)
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {t("crm.userManagement.userList")}
                </h3>
                <span className="text-sm text-muted-foreground">
                  {t("crm.userManagement.totalUsers", { count: totalUsers })}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                {t("crm.userManagement.hint")}
              </div>
            </div>
            <ReplyModeAgentSelect
              agents={replyModeAgents}
              value={replyModeAgentId}
              onChange={handleReplyModeAgentChange}
              disabled={!canManageUsers || replyMode.busy}
              permissionDenied={!canManageUsers}
              loadFailed={replyModeAgentsLoadFailed}
            />
          </div>
          {!canManageUsers && (
            <ReplyModeSelectionActions
              workspace={workspace}
              readOnly
              agent={selectedReplyModeAgent}
              controller={replyMode}
              selectionCount={0}
              onPrepare={handlePrepareReplyMode}
              onRecheck={handleRecheckReplyModeSnapshot}
              onConfirm={handleConfirmReplyMode}
            />
          )}

          {canManageUsers && (
            <ReplyModeBatchResults
              controller={replyMode}
              getCustomerLabel={getReplyModeCustomerLabel}
              onDismiss={replyMode.reset}
            />
          )}

          {canManageUsers && hasSelection && (
            <div className="divide-y rounded-lg border bg-muted/30">
              <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="space-y-1">
                  <div role="status" aria-live="polite" className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {allMatchingBatch
                        ? t(`${RM}.selection.allMatchingSelected`, {
                            count: allMatchingBatch.total,
                          })
                        : t("crm.userManagement.bulkActions.selectedCount", {
                            count: selectedClientUserIds.size,
                          })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectionHint}
                    </div>
                  </div>
                  {!allMatchingBatch && allOnPageSelected && totalUsers > 0 && (
                    <div className="flex flex-wrap items-center gap-x-2">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto px-0"
                        disabled={!replyModeAgentId || !replyMode.canCreate}
                        aria-describedby={
                          replyModeAgentId
                            ? undefined
                            : "crm-select-all-matching-needs-agent"
                        }
                        onClick={() => void handleSelectAllMatching()}
                      >
                        {t(`${RM}.selection.selectAllMatching`, {
                          count: totalUsers,
                        })}
                      </Button>
                      {!replyModeAgentId && (
                        <span
                          id="crm-select-all-matching-needs-agent"
                          className="text-xs text-muted-foreground"
                        >
                          {t(`${RM}.selection.selectAllMatchingNeedsAgent`)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={bulkActionLoading || replyMode.busy}
                >
                  {t("crm.userManagement.bulkActions.clear")}
                </Button>
              </div>
              <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-4 py-3">
                <ReplyModeSelectionActions
                  workspace={workspace}
                  agent={selectedReplyModeAgent}
                  controller={replyMode}
                  selectionCount={selectionCount}
                  onPrepare={handlePrepareReplyMode}
                  onRecheck={handleRecheckReplyModeSnapshot}
                  onConfirm={handleConfirmReplyMode}
                />
                <div className="flex flex-col gap-2">
                  <div
                    role="group"
                    aria-label={t(`${RM}.selection.accountGroup`)}
                    aria-describedby={
                      allMatchingBatch
                        ? "crm-account-actions-page-only"
                        : undefined
                    }
                    className="flex flex-wrap items-center gap-2"
                  >
                    <span className="text-xs font-medium text-muted-foreground">
                      {t(`${RM}.selection.accountGroup`)}
                    </span>
                    {bulkAccountActions.map(({ key, variant, onClick }) => (
                      <Button
                        key={key}
                        variant={variant}
                        size="sm"
                        onClick={onClick}
                        disabled={bulkActionLoading || !!allMatchingBatch}
                      >
                        {t(`crm.userManagement.bulkActions.${key}`)}
                      </Button>
                    ))}
                  </div>
                  {allMatchingBatch && (
                    <p
                      id="crm-account-actions-page-only"
                      className="max-w-sm text-xs text-muted-foreground"
                    >
                      {t(`${RM}.selection.accountActionsPageOnly`)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-md border">
            <Table className="min-w-[760px] md:min-w-[1040px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {canManageUsers && (
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        checked={allOnPageSelected}
                        disabled={!!allMatchingBatch || isCreatingAllMatching}
                        onChange={(e) =>
                          toggleSelectAllOnPage(e.target.checked)
                        }
                        aria-checked={
                          someOnPageSelected ? "mixed" : allOnPageSelected
                        }
                        aria-label={t(`${RM}.table.selectPage`)}
                        title={t(`${RM}.table.selectPage`)}
                      />
                    )}
                  </TableHead>
                  <TableHead className="w-[220px] whitespace-nowrap md:w-[240px]">
                    {t("crm.userManagement.table.identity")}
                  </TableHead>
                  <TableHead className="hidden w-24 whitespace-nowrap md:table-cell">
                    {t("crm.userManagement.table.channel")}
                  </TableHead>
                  <TableHead className="w-[220px] whitespace-nowrap md:w-[240px]">
                    {t("crm.userManagement.table.tags")}
                  </TableHead>
                  <TableHead className="w-32 whitespace-nowrap">
                    {t("crm.userManagement.table.accessStatus")}
                  </TableHead>
                  <TableHead className="hidden w-28 whitespace-nowrap lg:table-cell">
                    {t("crm.userManagement.table.interactionMode")}
                  </TableHead>
                  <TableHead className="hidden w-24 whitespace-nowrap md:table-cell">
                    <button
                      onClick={() => handleSort("conversation_count")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {t("crm.userManagement.table.conversationCount")}
                      {sortField === "conversation_count" ? (
                        sortOrder === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      ) : (
                        <ChevronUp className="h-4 w-4 opacity-30" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="hidden w-44 whitespace-nowrap lg:table-cell">
                    <button
                      onClick={() => handleSort("last_login_at")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {t("crm.userManagement.table.lastLoginAt")}
                      {sortField === "last_login_at" ? (
                        sortOrder === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      ) : (
                        <ChevronUp className="h-4 w-4 opacity-30" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="w-14 whitespace-nowrap text-right">
                    {t("crm.userManagement.actions.remove")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listClientUsersStatus === FetchStatus.Loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {users.length === 0 &&
                  listClientUsersStatus !== FetchStatus.Loading && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center text-muted-foreground"
                      >
                        {t("crm.userManagement.emptyState.noUsers")}
                      </TableCell>
                    </TableRow>
                  )}
                {users.map((user, index) => (
                  <TableRow
                    key={user.id}
                    className={`group cursor-pointer hover:bg-muted/50 ${
                      index % 2 === 0 ? "bg-background" : "bg-muted/30"
                    } ${isUserChecked(user.id) ? "bg-primary/10" : ""}`}
                    onClick={() => handleUserClick(user.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canManageUsers && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={isUserChecked(user.id)}
                          disabled={!!allMatchingBatch || isCreatingAllMatching}
                          onChange={(e) =>
                            toggleSelectUser(user.id, e.target.checked)
                          }
                          aria-label={t(`${RM}.table.selectRow`, {
                            name: getUserPrimaryDisplayName(user),
                          })}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex max-w-[220px] items-start gap-2 md:max-w-[240px]">
                        <div className="min-w-0 space-y-1">
                          <div className="truncate font-medium text-foreground">
                            {getUserPrimaryDisplayName(user)}
                          </div>
                          {getUserIdentityLines(user).map((identity) => (
                            <div
                              key={`${identity.labelKey}:${identity.value}`}
                              className="truncate text-xs text-muted-foreground"
                            >
                              {t(identity.labelKey)}: {identity.value}
                            </div>
                          ))}
                          {hasInvalidGoogleLoginIdentity(user) && (
                            <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
                              {t(
                                "crm.userManagement.table.invalidWebLoginIdentity"
                              )}
                            </div>
                          )}
                          {hasAmbiguousWebLoginIdentity(user) && (
                            <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
                              {t(
                                "crm.userManagement.table.ambiguousWebLoginIdentity"
                              )}
                            </div>
                          )}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-0.5 hidden h-7 w-7 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 sm:inline-flex"
                                onClick={(e) => handleUserClick(user.id, e)}
                                aria-label={
                                  canManageUsers
                                    ? t(
                                        "crm.userManagement.actions.viewAndEdit"
                                      )
                                    : t(
                                        "crm.userManagement.actions.viewDetails"
                                      )
                                }
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {canManageUsers
                                ? t("crm.userManagement.actions.viewAndEdit")
                                : t("crm.userManagement.actions.viewDetails")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {isClientChannelType(user.client_channel_type) ? (
                        <ChannelTypeBadge type={user.client_channel_type} />
                      ) : (
                        <span className="text-muted-foreground">
                          {t("crm.userManagement.table.unspecifiedChannel")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ClientUserTagInlineEditor
                        user={user}
                        tags={clientUserTags}
                        canManage={canManageUsers}
                        saving={tagUpdatingUserIds.has(user.id)}
                        open={openTagEditorUserId === user.id}
                        onOpenChange={(open) =>
                          handleTagEditorOpenChange(user.id, open)
                        }
                        onAddTag={handleAddUserTag}
                        onRemoveTag={handleRemoveUserTag}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex items-center gap-2">
                              <Switch
                                checked={user.status === "active"}
                                disabled={
                                  !canManageUsers ||
                                  statusUpdatingUserIds.has(user.id)
                                }
                                onCheckedChange={() =>
                                  void handleToggleStatus(user.id, user.status)
                                }
                                aria-label={getAccessToggleLabel(user)}
                              />
                              <Badge
                                variant={getStatusBadgeVariant(user.status)}
                                className={getStatusBadgeClassName(user.status)}
                              >
                                {user.status === "active"
                                  ? t("crm.userManagement.filters.active")
                                  : t("crm.userManagement.filters.blocked")}
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {!canManageUsers
                              ? t("common.noPermission")
                              : getAccessToggleLabel(user)}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant={getInteractionSummaryBadgeVariant(user)}
                              className={getInteractionSummaryBadgeClassName(
                                user
                              )}
                            >
                              {getInteractionSummaryLabel(user)}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-w-48 text-xs">
                              {getInteractionSummaryDescription(user) ||
                                t("crm.userManagement.interactionSummary.help")}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {user.conversation_count || 0}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getLastActiveAt(user)
                        ? new Date(getLastActiveAt(user) || "").toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                disabled={!canManageUsers}
                                onClick={(e) =>
                                  handleRemoveUserClick(
                                    user.id,
                                    user.name,
                                    user.email || user.external_user_id,
                                    e
                                  )
                                }
                                aria-label={t(
                                  "crm.userManagement.actions.remove"
                                )}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {canManageUsers
                              ? t("crm.userManagement.actions.remove")
                              : t("common.noPermission")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <PageNav
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}

      <Dialog
        open={showAddUserDialog && !workspace.maskEndUserIdentity}
        onOpenChange={(open) => {
          setShowAddUserDialog(open)
          if (!open) {
            setAddUserExternalUserId("")
            setAddUserEmail("")
            setAddUserName("")
            setAddUserPhone("")
            setAddUserCompany("")
            setAddUserJobTitle("")
            setAddUserNote("")
            setAddUserChannelValue(defaultClientUserChannelValue)
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              {t("crm.userManagement.addUser.title")}
            </DialogTitle>
            <DialogDescription>
              {t(
                usesGoogleLoginEmail
                  ? "crm.userManagement.addUser.googleLoginDescription"
                  : "crm.userManagement.addUser.description"
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label className="flex items-center gap-1.5">
                <span className="text-red-500">*</span>
                {t("crm.userManagement.addUser.channelType")}
              </Label>
              <Select
                value={addUserChannelValue}
                onValueChange={handleAddUserChannelChange}
              >
                <SelectTrigger
                  className={!addUserChannelType ? "border-red-500" : ""}
                >
                  <SelectValue
                    placeholder={t(
                      "crm.userManagement.addUser.channelTypePlaceholder"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {clientUserChannelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {getClientUserChannelOptionLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t(
                  usesGoogleLoginEmail
                    ? "crm.userManagement.addUser.googleLoginChannelHelper"
                    : "crm.userManagement.addUser.channelTypeHelper"
                )}
              </p>
              {!addUserChannelType && (
                <div className="text-sm text-red-500">
                  {t("crm.userManagement.addUser.channelTypeRequired")}
                </div>
              )}
            </div>
            {usesGoogleLoginEmail ? (
              <div className="space-y-2 sm:col-span-2">
                <Label className="flex items-center gap-1.5">
                  <span className="text-red-500">*</span>
                  {t("crm.userManagement.addUser.googleLoginEmail")}
                </Label>
                <Input
                  value={addUserEmail}
                  onChange={(e) => setAddUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  className={
                    normalizedAddUserEmail && singleUserValidation.isEmailValid
                      ? ""
                      : "border-red-500"
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t("crm.userManagement.addUser.googleLoginEmailHelper")}
                </p>
                {!normalizedAddUserEmail ? (
                  <div className="text-sm text-red-500">
                    {t("crm.userManagement.addUser.googleLoginEmailRequired")}
                  </div>
                ) : (
                  !singleUserValidation.isEmailValid && (
                    <div className="text-sm text-red-500">
                      {t("crm.userManagement.addUser.emailInvalid")}
                    </div>
                  )
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="flex items-center gap-1.5">
                    <span className="text-red-500">*</span>
                    {t(addUserIdentityField.labelKey)}
                  </Label>
                  <Input
                    value={addUserExternalUserId}
                    onChange={(e) => setAddUserExternalUserId(e.target.value)}
                    placeholder={addUserIdentityField.placeholder}
                    className={
                      singleUserValidation.isExternalUserIdValid &&
                      isAddUserChannelIdentityValid
                        ? ""
                        : "border-red-500"
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(addUserIdentityField.helperKey)}
                  </p>
                  {!singleUserValidation.isExternalUserIdValid ? (
                    <div className="text-sm text-red-500">
                      {t("crm.userManagement.addUser.identityRequired", {
                        field: t(addUserIdentityField.labelKey),
                      })}
                    </div>
                  ) : (
                    !isAddUserChannelIdentityValid && (
                      <div className="text-sm text-red-500">
                        {t("crm.userManagement.addUser.identityInvalid", {
                          field: t(addUserIdentityField.labelKey),
                        })}
                      </div>
                    )
                  )}
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="flex items-center gap-1.5">
                    {t("crm.userManagement.addUser.email")}
                  </Label>
                  <Input
                    value={addUserEmail}
                    onChange={(e) => setAddUserEmail(e.target.value)}
                    placeholder="john@example.com"
                    className={
                      singleUserValidation.isEmailValid ? "" : "border-red-500"
                    }
                  />
                  {!singleUserValidation.isEmailValid && (
                    <div className="text-sm text-red-500">
                      {t("crm.userManagement.addUser.emailInvalid")}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="space-y-2 sm:col-span-2">
              <Label className="flex items-center gap-1.5">
                <span className="text-red-500">*</span>
                {t("crm.userManagement.addUser.name")}
              </Label>
              <Input
                value={addUserName}
                onChange={(e) => setAddUserName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("crm.userManagement.addUser.phone")}</Label>
              <Input
                value={addUserPhone}
                onChange={(e) => setAddUserPhone(e.target.value)}
                placeholder="0912345678"
              />
              {!singleUserValidation.isPhoneValid && (
                <div className="text-sm text-red-500">
                  {t("crm.userManagement.addUser.phoneInvalid")}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t("crm.userManagement.addUser.company")}</Label>
              <Input
                value={addUserCompany}
                onChange={(e) => setAddUserCompany(e.target.value)}
                placeholder="ABC Corp"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("crm.userManagement.addUser.jobTitle")}</Label>
              <Input
                value={addUserJobTitle}
                onChange={(e) => setAddUserJobTitle(e.target.value)}
                placeholder="Product Manager"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("crm.userManagement.addUser.note")}</Label>
              <Textarea
                value={addUserNote}
                onChange={(e) => setAddUserNote(e.target.value)}
                rows={3}
                placeholder="VIP 客戶"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddUserDialog(false)}
              disabled={addUserSubmitting}
            >
              {t("crm.userManagement.detail.cancel")}
            </Button>
            <Button
              onClick={handleCreateSingleUser}
              disabled={
                !singleUserValidation.canSubmit ||
                !isAddUserChannelIdentityValid ||
                !addUserChannelType ||
                addUserSubmitting
              }
            >
              {addUserSubmitting ? (
                <>
                  <span className="mr-2">
                    {t("crm.userManagement.addUsers.csvImport.processing")}
                  </span>
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("crm.userManagement.addUser.add")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientUserTagManagerDialog
        open={showTagManagerDialog}
        tags={clientUserTags}
        loading={tagActionLoading}
        onOpenChange={setShowTagManagerDialog}
        onCreateTag={handleCreateClientUserTag}
        onRequestDeleteTag={handlePrepareDeleteClientUserTag}
      />

      <ClientUserTagDeleteDialog
        tag={tagToDelete}
        loading={tagActionLoading}
        onOpenChange={(open) => {
          if (!open && !tagActionLoading) {
            setTagToDelete(null)
          }
        }}
        onConfirm={handleDeleteClientUserTag}
      />

      <Dialog
        open={showCsvDialog && !workspace.maskEndUserIdentity}
        onOpenChange={(open) => {
          setShowCsvDialog(open)
          if (!open) {
            setCsvFile(null)
            setCsvPreview(null)
            setCsvChannelValue(defaultClientUserChannelValue)
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t("crm.userManagement.addUsers.csvImport.title")}
            </DialogTitle>
            <DialogDescription>
              {t("crm.userManagement.addUsers.csvImport.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <span className="text-red-500">*</span>
                {t("crm.userManagement.addUsers.csvImport.channelType")}
              </Label>
              <Select
                value={csvChannelValue}
                onValueChange={(value) => {
                  setCsvChannelValue(value)
                  setCsvPreview(null)
                }}
              >
                <SelectTrigger
                  className={!csvChannelType ? "border-red-500" : ""}
                >
                  <SelectValue
                    placeholder={t(
                      "crm.userManagement.addUsers.csvImport.channelTypePlaceholder"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {clientUserChannelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {getClientUserChannelOptionLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("crm.userManagement.addUsers.csvImport.channelTypeHelper")}
              </p>
              {csvChannelType && (
                <p
                  className={
                    usesGoogleLoginEmailForCsv
                      ? "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                      : "rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground"
                  }
                >
                  {t(csvIdentityField.helperKey)}
                </p>
              )}
              {!csvChannelType && (
                <div className="text-sm text-red-500">
                  {t(
                    "crm.userManagement.addUsers.csvImport.channelTypeRequired"
                  )}
                </div>
              )}
            </div>
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-2 text-sm">
                  <div>
                    <span className="font-medium">
                      {t(
                        "crm.userManagement.addUsers.csvImport.requiredFields"
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      {t(
                        "crm.userManagement.addUsers.csvImport.identityRequiredFields",
                        {
                          identity: t(csvIdentityField.labelKey),
                        }
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">
                      {t(
                        "crm.userManagement.addUsers.csvImport.optionalFields"
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      {t(
                        "crm.userManagement.addUsers.csvImport.optionalProfileFields"
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-background border p-3 font-mono text-xs">
                <div className="text-muted-foreground mb-1">
                  {t("crm.userManagement.addUsers.csvImport.example")}
                </div>
                <div className="space-y-1">
                  <div className="text-foreground">
                    External User ID,Email,Name,Phone,Company,Job Title,Note
                  </div>
                  <div className="text-muted-foreground">{csvExampleRow}</div>
                </div>
              </div>
            </div>

            <UploadDropzone
              file={csvFile}
              onFilesSelected={handleFilesSelected}
              disabled={!csvChannelType}
              selectFileLabel={t(
                "crm.userManagement.addUsers.csvImport.selectFile"
              )}
              supportedFormatLabel={t(
                "crm.userManagement.addUsers.csvImport.supportedFormat"
              )}
              chooseFileLabel={t(
                "crm.userManagement.addUsers.csvImport.chooseFile"
              )}
              rechooseFileLabel={t(
                "crm.userManagement.addUsers.csvImport.rechooseFile"
              )}
            />

            {csvPreviewLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">
                  {t("crm.userManagement.addUsers.csvImport.loading")}
                </div>
              </div>
            )}

            {csvPreview && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-600">
                        {t(
                          "crm.userManagement.addUsers.csvImport.successCount",
                          {
                            count: csvPreview.success,
                          }
                        )}
                      </span>
                    </div>
                    {csvPreview.failed > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="font-medium text-red-600">
                          {t(
                            "crm.userManagement.addUsers.csvImport.failedCount",
                            {
                              count: csvPreview.failed,
                            }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {csvPreview.rows.length > 0 && (
                  <div className="rounded-md border overflow-hidden">
                    <div className="max-h-60 overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted/50 border-b">
                          <tr>
                            <th className="p-3 text-left font-medium">#</th>
                            <th className="p-3 text-left font-medium">
                              {t("crm.userManagement.addUser.externalUserId")}
                            </th>
                            <th className="p-3 text-left font-medium">Email</th>
                            <th className="p-3 text-left font-medium">Name</th>
                            <th className="p-3 text-left font-medium">
                              {t(
                                "crm.userManagement.addUsers.csvImport.status"
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.rows.slice(0, 50).map((r) => (
                            <tr
                              key={r.row}
                              className="border-b hover:bg-muted/30 transition-colors"
                            >
                              <td className="p-3 text-muted-foreground">
                                {r.row}
                              </td>
                              <td className="p-3">
                                {r.external_user_id || "-"}
                              </td>
                              <td className="p-3">{r.email || "-"}</td>
                              <td className="p-3">{r.name || "-"}</td>
                              <td className="p-3">
                                {r.ok ? (
                                  <Badge
                                    variant="default"
                                    className="bg-green-600"
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {t(
                                      "crm.userManagement.addUsers.csvImport.success"
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge variant="destructive">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {translateCsvError(r.error)}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {csvPreview.rows.length > 50 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 border-t">
                        {t(
                          "crm.userManagement.addUsers.csvImport.showingFirst",
                          {
                            first: 50,
                            total: csvPreview.rows.length,
                          }
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCsvDialog(false)}
              disabled={csvImportSubmitting}
            >
              {t("crm.userManagement.detail.cancel")}
            </Button>
            <Button
              onClick={handleConfirmCsv}
              disabled={
                !csvFile ||
                !csvChannelType ||
                csvImportSubmitting ||
                csvPreviewLoading ||
                !csvPreview
              }
            >
              {csvImportSubmitting ? (
                <>
                  <span className="mr-2">
                    {t("crm.userManagement.addUsers.csvImport.processing")}
                  </span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t("crm.userManagement.addUsers.upload")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.confirm.removeUserTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToRemove?.name ? (
                <>
                  {t("crm.userManagement.confirm.removeUserWithoutName")}
                  <span className="text-red-500 font-medium">
                    {userToRemove.name}
                  </span>
                  {t("crm.userManagement.confirm.removeUserSuffix")}
                </>
              ) : (
                <>
                  {t("crm.userManagement.confirm.removeUserWithoutName")}
                  <span className="text-red-500 font-medium">
                    {userToRemove?.email}
                  </span>
                  {t("crm.userManagement.confirm.removeUserSuffix")}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToRemove(null)}>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemoveUser}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("crm.userManagement.detail.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showIdentityMaskingConfirmDialog}
        onOpenChange={setShowIdentityMaskingConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.identityMasking.confirm.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {t("crm.userManagement.identityMasking.confirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdatingIdentityMasking}>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmIdentityMasking}
              disabled={isUpdatingIdentityMasking}
            >
              {t("crm.userManagement.identityMasking.confirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showBulkRemoveDialog}
        onOpenChange={setShowBulkRemoveDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.confirm.bulkRemoveUsersTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("crm.userManagement.confirm.bulkRemoveUsersDescription", {
                count: selectedClientUserIds.size,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmBulkRemoveUsers}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkActionLoading}
            >
              {t("crm.userManagement.bulkActions.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showAccessModeConfirmDialog}
        onOpenChange={(open) => {
          setShowAccessModeConfirmDialog(open)
          if (!open) setPendingAccessMode(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.accessMode.confirmSwitch.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("crm.userManagement.accessMode.confirmSwitch.description", {
                count: listClientUsersData?.pagination?.total_records ?? 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAccessModeChange}
              disabled={bulkActionLoading}
            >
              {t("crm.userManagement.accessMode.confirmSwitch.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showAccessModeOpenWarningDialog}
        onOpenChange={(open) => {
          setShowAccessModeOpenWarningDialog(open)
          if (!open) setPendingAccessMode(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.accessMode.confirmOpen.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {t("crm.userManagement.accessMode.confirmOpen.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowAccessModeOpenWarningDialog(false)
                setShowAccessModeOpenConfirmDialog(true)
              }}
            >
              {t("crm.userManagement.accessMode.confirmOpen.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showAccessModeOpenConfirmDialog}
        onOpenChange={(open) => {
          setShowAccessModeOpenConfirmDialog(open)
          if (!open) {
            setOpenConfirmInput("")
            setPendingAccessMode(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("crm.userManagement.accessMode.confirmOpenName.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("crm.userManagement.accessMode.confirmOpenName.description", {
                workspaceName: workspace.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 mt-4">
            <Input
              value={openConfirmInput}
              onChange={(e) => setOpenConfirmInput(e.target.value)}
              placeholder={t(
                "crm.userManagement.accessMode.confirmOpenName.placeholder"
              )}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("crm.userManagement.detail.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setIsUpdatingAccessMode(true)
                try {
                  await updateClientAccess({ jsonBody: { mode: "open" } })
                  setAccessMode("open")
                  toast({ title: t("crm.userManagement.messages.modeUpdated") })
                  setShowAccessModeOpenConfirmDialog(false)
                  setPendingAccessMode(null)
                  setOpenConfirmInput("")
                } catch (error) {
                  showApiErrorToast(
                    error,
                    t("crm.userManagement.errors.updateMode")
                  )
                } finally {
                  setIsUpdatingAccessMode(false)
                }
              }}
              disabled={
                openConfirmInput.trim() !== workspace.name ||
                isUpdatingAccessMode
              }
            >
              {t("crm.userManagement.accessMode.confirmOpenName.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default UserManagementTab
